import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Eye, EyeOff, BookOpen, MessageSquare, TrendingUp, Users } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUILD_VERSION = '2026.06.28.r1'; // bump to force a fresh production bundle

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [tiles, setTiles] = useState([]);

  useEffect(() => {
    axios.get(`${API}/welcome-tiles`)
      .then((res) => setTiles(res.data || []))
      .catch(() => setTiles([]));
  }, []);

  const tileSrc = (url) => (url && url.startsWith('http')) ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      
      login(response.data.user, password, response.data.token);
      const u = response.data.user;
      if (u?.role === 'demo') {
        navigate('/curriculum');
      } else if (!u?.last_login) {
        navigate('/welcome');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      // Distinguish network errors from auth errors so a misconfigured backend
      // URL doesn't masquerade as wrong credentials.
      if (!err.response) {
        setError(`Couldn't reach the server. Please check your connection and try again.`);
      } else if (err.response.status === 401) {
        setError('Invalid email or password. Please try again.');
      } else if (err.response.status === 403) {
        setError(err.response.data?.detail || 'Account is disabled.');
      } else {
        setError(err.response.data?.detail || 'Login failed. Please try again.');
      }
      console.error('Login error', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#34657f] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#34657f] to-[#2a5060]" />
        
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-32 h-32 bg-[#3d7a22] opacity-20 rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute top-1/4 right-0 w-48 h-48 bg-[#b34e00] opacity-20 rounded-full translate-x-1/2" />
        <div className="absolute bottom-0 left-1/4 w-40 h-40 bg-[#1f7fa6] opacity-20 rounded-full translate-y-1/2" />
        <div className="absolute bottom-1/4 right-1/4 w-24 h-24 bg-[#3d7a22] opacity-30 rounded-full" />
        
        <div className="relative z-10 flex flex-col justify-center items-center w-full p-12 text-white">
          {/* Logo */}
          <div className="bg-white rounded-2xl shadow-lg px-8 py-5 mb-8">
            <img 
              src="/lifelink-logo.png" 
              alt="LIFE Link Logo" 
              className="h-24 object-contain"
            />
          </div>
          
          <p className="text-xl text-white/90 text-center max-w-md mb-12">
            Preparation for LIFE — Helping students with disabilities build work and life skills
          </p>
          
          {/* Feature Image (single photo, editable via CMS) */}
          {tiles[0] && (
            <div className="relative w-full max-w-lg h-[440px] rounded-2xl overflow-hidden group ring-1 ring-white/10 shadow-2xl">
              <img
                src={tileSrc(tiles[0].image_url)}
                alt={tiles[0].title || 'Project LIFE'}
                style={{ objectPosition: tiles[0].focal_point || '50% 50%' }}
                className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <main id="main-content" tabIndex={-1} className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img 
              src="/lifelink-logo.png" 
              alt="LIFE Link Logo" 
              className="h-20 object-contain"
            />
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="text-center pb-6">
              <CardTitle className="font-['Manrope'] text-3xl font-bold text-[#1A1A1A]">
                Welcome Back
              </CardTitle>
              <CardDescription className="text-base text-[#5C5C5C]">
                Sign in to LIFE Link — your Project LIFE portal
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@projectlife.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 px-4 border-slate-300 focus:border-[#34657f] focus:ring-2 focus:ring-[#34657f]/20"
                    data-testid="login-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-sm font-medium">
                      Password
                    </Label>
                    <a
                      href="/forgot-password"
                      className="text-sm text-[#34657f] font-medium underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </a>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 px-4 pr-12 border-slate-300 focus:border-[#34657f] focus:ring-2 focus:ring-[#34657f]/20"
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded text-slate-500 hover:text-slate-700"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="text-red-700 text-sm text-center" role="alert" data-testid="login-error">
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 bg-[#34657f] hover:bg-[#2a5060] text-white font-semibold rounded-lg transition-all"
                  data-testid="login-submit-btn"
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-[#5C5C5C] mt-8">
            &copy; 2026 LIFE Link | Project LIFE | Butler Tech. All rights reserved.
          </p>
          <p className="text-center text-sm mt-2 space-x-3">
            <a
              href="/privacy"
              className="text-[#1f7fa6] underline font-medium"
              data-testid="privacy-link"
            >
              Privacy Policy
            </a>
            <a
              href="/terms"
              className="text-[#1f7fa6] underline font-medium"
              data-testid="terms-link"
            >
              Terms &amp; Conditions
            </a>
            <a
              href="/accessibility"
              className="text-[#1f7fa6] underline font-medium"
              data-testid="accessibility-statement-link"
            >
              Accessibility
            </a>
          </p>
          <p className="text-center text-xs text-[#6b7280] mt-2" data-testid="build-version">
            build {BUILD_VERSION}
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
